import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface OnboardingStep {
  title: string;
  type: "image" | "video";
  imageUrl?: string;
  videoUrl?: string;
}

const onboardingSteps: OnboardingStep[] = [
  {
    title: "Catch Listings\nBefore The Crowd",
    type: "image",
    imageUrl: "/onboarding/1.png",
  },
  {
    title: "Get In Before It Pumps",
    type: "image",
    imageUrl: "/onboarding/2.gif",
  },
  {
    title: "Take Profit When It Pumps.",
    type: "image",
    imageUrl: "/onboarding/3.gif",
  },
];

export const OnboardingPageDesktop: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const videosRefs = useRef<HTMLVideoElement | null[]>([]);

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      videosRefs.current[currentStep + 1]?.play();
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 50);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    localStorage.setItem(
      `onboarding-completed-${import.meta.env.VITE_APP_VERSION}`,
      "true"
    );

    // Dispatch custom event to notify App of onboarding completion
    window.dispatchEvent(new CustomEvent("onboarding-completed"));

    // Navigate directly to referral-entry
    navigate("/referral-entry");
  };

  const currentStepData = onboardingSteps[currentStep];
  const isLastStep = currentStep === onboardingSteps.length - 1;

  return (
    <div className="min-h-screen bg-primary flex">
      {/* Empty main content */}
      <div className="flex-1"></div>

      {/* Modal Overlay - Blocks all clicks outside */}
      <div
        className="fixed inset-0 bg-black/80 z-[100]"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Centered Dialog */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-8">
        <div
          className="bg-secondary rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-2xl font-bold text-text-primary text-center">
              Welcome
            </h2>
          </div>

          {/* Content */}
          <div className="p-6 flex flex-col gap-6">
            {/* Media Display Area */}
            <div className="relative bg-primary rounded-xl overflow-hidden aspect-video">
              {onboardingSteps.map(
                ({ type, videoUrl, imageUrl, title }, stepIndex) => (
                  <div
                    key={`step-${title}-${stepIndex}`}
                    className="absolute inset-0"
                  >
                    {type === "video" ? (
                      <video
                        ref={(el) => (videosRefs.current[stepIndex] = el)}
                        src={videoUrl}
                        style={{
                          display: currentStep === stepIndex ? "block" : "none",
                        }}
                        preload="auto"
                        muted
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <img
                        src={imageUrl}
                        alt="onboarding"
                        className="w-full h-full object-contain"
                        style={{
                          display: currentStep === stepIndex ? "block" : "none",
                        }}
                      />
                    )}
                  </div>
                )
              )}

              {/* Title Overlay */}
              <div className="absolute bottom-6 left-0 right-0">
                <div className="flex items-center justify-center px-4">
                  <span className="text-text-primary text-xl text-center font-semibold drop-shadow-lg">
                    {currentStepData.title
                      .split("\n")
                      .map((line, lineIndex) => (
                        <React.Fragment key={`line-${lineIndex}-${line}`}>
                          {line}
                          {lineIndex <
                            currentStepData.title.split("\n").length - 1 && (
                            <br />
                          )}
                        </React.Fragment>
                      ))}
                  </span>
                </div>
              </div>
            </div>

            {/* Stepper */}
            <div className="flex items-center justify-center space-x-0">
              {onboardingSteps.map((step, stepperIndex) => (
                <React.Fragment key={`stepper-${step.title}-${stepperIndex}`}>
                  {/* Dot */}
                  <div
                    className={`w-[24px] h-[24px] rounded-full transition-colors border-accent-light border-2 ${
                      stepperIndex <= currentStep ? "bg-accent-light" : ""
                    } flex items-center justify-center`}
                  >
                    <span
                      className={`${stepperIndex <= currentStep ? "text-black" : "text-accent-light"} text-sm font-semibold`}
                    >
                      {stepperIndex + 1}
                    </span>
                  </div>

                  {/* Line (except after last dot) */}
                  {stepperIndex < onboardingSteps.length - 1 && (
                    <div className="w-[24px] h-0.5 bg-accent-light" />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={handleNext}
              className={`w-full py-3 ${
                isLastStep
                  ? "bg-accent hover:bg-accent/90"
                  : "bg-primary hover:bg-primary/80 border-2 border-border"
              } text-text-primary font-semibold rounded-xl transition-all`}
            >
              {isLastStep ? "Get Started" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
